import React, { useEffect, useState, useMemo } from "react";
import Button from "../../form/Button";
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { accountCheck, eventDetails } from "../../../services/eventService";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import Image from "../../elements/Image";
import { TiptapContent } from "../../elements/tiptap-editor/EditorComponent";
import { toast } from "react-toastify";
import ContactModal from "../../common/ContactModal";
import PricingPlanCard from "./PricingPlanCard";
import ReturnPolicy from "./ReturnPolicy";
import { classNames } from "../../../helpers/classNames";
import RatingStars from "../../common/RatingStars";
import ReviewPreview from "../../common/ReviewPreview";
import OrganizationReviewModal from "../../common/OrganizationReviewModal";
import { organizationReviewList } from "../../../services/reviewService";
import dayjs from "dayjs";
import AllReviewsModal from "../../common/AllReviewModal";
import { lngDetect } from "../../../helpers/lngDetect";
import EventReportModal from "./event-manage/EventReportModal";
import {
  organizationFollow,
  followList,
  organizationUnfollow,
} from "../../../services/followService";
import ArtistItem from "./Following/ArtistItem";
import NoData from "../../elements/Nodata";
import noDataImage from "../../../assets/images/tickets.svg";
import { chatCreate } from "../../../services/chatService";
import { useTranslation } from "react-i18next";

const AboutEvent = ({ event = {}, eventOrganizer = {} }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { eventId } = useParams();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isAllReviewsModalOpen, setIsAllReviewsModalOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  const { lang } = useSelector((state) => state?.langProvider);
  const { data } = useSelector((state) => state?.profile);
  const queryClient = useQueryClient();
  const [organizationFollowerCount, setOrganizationFollowerCount] = useState(0);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });
  const [reviewFilters, setReviewFilters] = useState({
    limit: 3,
    page: 1,
    sortQuery: "-createdAt",
  });
  const navigate = useNavigate();

  // Check follow status
  const {
    data: followData,
    isLoading: followLoading,
    isFetching: followFetching,
  } = useQuery({
    queryKey: ["follow-list", filters, data?._id, eventOrganizer?.data?._id],
    queryFn: async () => {
      const res = await followList({
        ...filters,
        organization: eventOrganizer?.data?._id,
        referenceType: "organization",
        user: data?._id,
      });

      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    enabled: !!data?._id && !!eventOrganizer?.data?._id,
  });

  // // Update follow status based on followData
  useEffect(() => {
    if (followData?.metadata) {
      setOrganizationFollowerCount(followData?.metadata?.totalDocs);
    }
  }, [followData, eventOrganizer?.data?._id]);

  // Update follow status based on followData
  useEffect(() => {
    if (followData?.data && Array.isArray(followData.data)) {
      const isUserFollowing = followData.data.some(
        (follow) =>
          follow.from === data?._id &&
          follow.to === eventOrganizer?.data?._id &&
          follow.referenceType === "organization"
      );
      setIsFollowing(isUserFollowing);
    }
  }, [followData, data?._id, eventOrganizer?.data?._id]);

  // Follow/Unfollow mutation
  const { mutate: handleFollowToggle, isPending: isFollowLoading } =
    useMutation({
      mutationFn: async () => {
        if (!data) {
          navigate(`/login`);
        }

        if (!eventOrganizer?.data?._id) {
          throw new Error("Organization not found");
        }

        const action = isFollowing ? "unfollow" : "follow";

        const payload = {
          fromId: data?._id,
          toId: eventOrganizer?.data?._id,
          referenceType: "organization",
        };

        // Call the appropriate service function
        const response = isFollowing
          ? await organizationUnfollow(payload)
          : await organizationFollow(payload);

        return { ...response, action };
      },
      onSuccess: async (responseData) => {
        if (responseData?.status === 200 || responseData?.status === 201) {
          const { action } = responseData;
          setIsFollowing(action === "follow");

          // Invalidate follow list query to refresh the data
          queryClient.invalidateQueries({
            queryKey: [
              "follow-list",
              filters,
              data?._id,
              eventOrganizer?.data?._id,
            ],
          });
          return responseData;
        } else {
          toast.warning(responseData?.message || responseData?.error);
        }
        return responseData;
      },
      onError: (error) => {
        toast.error(
          error?.message || "Something went wrong. Please try again."
        );
      },
    });

  const eventSections = [
    {
      title: t("About This Event"),
      content: (
        <div className="space-y-8">
          <div>
            {event?.address?.name && (
              <div className="text-text-secondary text-base mb-4 flex items-start gap-3">
                <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
                  <i className="fa-regular fa-location-dot text-primary"></i>
                </span>
                <span className="flex flex-shrink flex-grow pt-1">
                  {event?.address?.name || ""}
                </span>
              </div>
            )}
            <div className="text-text-secondary text-base mb-2 flex items-start gap-3">
              <span className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/10">
                <i className="fa-regular fa-calendar-days text-primary"></i>
              </span>
              <span className="flex flex-shrink flex-grow pt-1">
                {dayjs(event?.startDate)?.format("DD MMM")} -{" "}
                {dayjs(event?.endDate)?.format("DD MMM YYYY")}
              </span>
            </div>
          </div>
          <div className="space-y-3">
            {lngDetect(event?.excerpt)}
            {event?.overView?.map((over_view, i) => (
              <div key={over_view?._id}>
                {over_view?.type === "text" && (
                  <TiptapContent data={lngDetect(over_view?.content)} />
                )}
                {over_view?.type === "image" && over_view?.file?.url && (
                  <Image
                    src={over_view?.file?.url}
                    effect={"fade"}
                    alt={over_view?.file?.url}
                  />
                )}
                {over_view?.type === "link" && (
                  <a
                    rel="noreferrer"
                    target="_blank"
                    href={over_view?.url || "#"}
                  >
                    {over_view?.url}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      ),
    },
    ...(event?.tagData?.length > 0
      ? [
          {
            title: t("Tags"),
            tags: event?.tagData?.map((item) => lngDetect(item?.name)) || [],
          },
        ]
      : []),
  ];

  const pricingPlan = {
    title: t("Book Your Tickets"),
    // price: `$${event?.minTicketPrice}`,
    // originalPrice: `$${event?.maxTicketPrice}`,
    description: lngDetect(event?.title),
    featureHTML: `
      <ul>
        <li>Up to 3 events/month</li>
        <li>Basic analytics</li>
        <li>Email support</li>
      </ul>
    `,
    button: {
      label: t("get started"),
      link: `/${lang}/order-summary/${eventId}`,
    },
  };

  const {
    data: accountData,
    isLoading: isAccountLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["account-status", eventOrganizer?.data?.userData?._id],
    queryFn: async () => {
      const res = await accountCheck({
        user: eventOrganizer?.data?.userData?._id,
      });
      if (res?.status !== 200) {
        toast.warning(res?.error || res?.message);
      }
      return res?.data;
    },
    enabled: !!eventOrganizer?.data?.userData?._id,
    onError: (err) => {
      toast.error(
        err?.response?.data?.message || "Failed to check Stripe account status"
      );
    },
  });

  // Fetch limited reviews for preview
  const {
    data: reviewList,
    isLoading: reviewsLoading,
    refetch: refetchReviews,
  } = useQuery({
    queryKey: ["review-list-preview", reviewFilters, eventOrganizer?.data?._id],
    queryFn: async () => {
      const res = await organizationReviewList({
        ...reviewFilters,
        referenceId: eventOrganizer?.data?._id,
        status: ["approved", "pending"],
      });
      if (res?.status !== 200) {
        toast.warning(res?.error || res?.message);
      }
      return res?.docs;
    },
    enabled: !!eventOrganizer?.data?._id,
  });

  // Calculate organizer rating and format reviews
  const organizerRatingData = useMemo(() => {
    if (!reviewList?.data || reviewList.data.length === 0) {
      return {
        rating: 0,
        totalReviews: 0,
        reviews: [],
        averageRating: 0,
        ratingBreakdown: {
          5: 0,
          4: 0,
          3: 0,
          2: 0,
          1: 0,
        },
      };
    }

    const reviews = reviewList.data.map((item) => ({
      id: item._id,
      userName: item.isAnonymous
        ? "Anonymous"
        : `${item?.userData?.firstName ?? ""} ${
            item?.userData?.lastName ?? ""
          }`.trim() || "Anonymous",
      rating: item?.overallRating || item?.rating || 0,
      comment: item?.review || item?.message || "",
      date: dayjs(item?.createdAt).format("YYYY-MM-DD"),
      isVerified: item?.userData?.isVerified || false,
      isAnonymous: item?.isAnonymous || false,
      categoryRatings: item?.categoryRatings || {},
      wouldRecommend: item?.wouldRecommend || false,
      userAvatar: item?.userData?.avatar?.url || null,
    }));

    const totalReviews = eventOrganizer?.data?.totalReviews || reviews.length;
    const averageRating = eventOrganizer?.data?.averageRating || 0;

    const ratingBreakdown = {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0,
    };

    reviews.forEach((review) => {
      const roundedRating = Math.round(review.rating);
      if (roundedRating >= 1 && roundedRating <= 5) {
        ratingBreakdown[roundedRating]++;
      }
    });

    return {
      rating: averageRating,
      totalReviews: totalReviews,
      reviews: reviews,
      averageRating: averageRating,
      ratingBreakdown: ratingBreakdown,
      totalPages: reviewList?.metadata?.totalPages || 1,
      hasMore: reviewList?.metadata?.hasNextPage || false,
      recommendationPercentage:
        reviews.length > 0
          ? Math.round(
              (reviews.filter((r) => r.wouldRecommend).length /
                reviews.length) *
                100
            )
          : 0,
    };
  }, [reviewList]);

  const isAccountFullyEnabled =
    accountData?.data?.stripe || accountData?.data?.paypal;

  const handleOpenReviewModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (data) {
      setIsReviewModalOpen(true);
    } else {
      navigate(`/login`);
    }
  };

  const handleSeeAllReviews = () => {
    setIsAllReviewsModalOpen(true);
  };

  const handleReviewSuccess = async () => {
    try {
      eventOrganizer.isReview = true;
      await queryClient.invalidateQueries({
        queryKey: [
          "review-list-preview",
          reviewFilters,
          eventOrganizer?.data?._id,
        ],
      });

      await queryClient.invalidateQueries({
        queryKey: ["all-reviews", eventOrganizer?.data?._id],
        exact: false,
      });

      if (eventOrganizer?.data?._id) {
        await refetchReviews();
      }
    } catch (error) {
      toast.info("Review submitted! Please refresh to see the latest reviews.");
    }
  };

  const handleCloseReviewModal = () => {
    setIsReviewModalOpen(false);
  };

  const handleCloseAllReviewsModal = () => {
    setIsAllReviewsModalOpen(false);
    if (eventOrganizer?.data?._id) {
      queryClient.invalidateQueries({
        queryKey: [
          "review-list-preview",
          reviewFilters,
          eventOrganizer?.data?._id,
        ],
      });
    }
  };

  function getFollowManageRes(res) {
    if (res?.status === 200) {
      queryClient.setQueryData(
        ["event-details", eventId, location?.key],
        (state) => {
          let state_data = JSON.parse(JSON.stringify(state || {}));
          state_data.data.artistData = state_data.data.artistData?.map((item) =>
            item?.user === res?.data?.to
              ? { ...item, isFollowing: res?.data?.status !== "cancelled" }
              : item
          );
          return { ...state_data };
        }
      );
    }
  }

  const { mutate: chatStartHandler } = useMutation({
    mutationFn: async (val) => {
      const res = chatCreate({ event: event?._id });
      return res;
    },
    onSuccess: (res) => {
      if (res?.status === 201 || res?.status === 200) {
        navigate(`/${lang}/user/communication?id=${res?.data?.uid}`);
      } else {
        toast.warning(res?.error || res?.message || "Something went wrong");
      }
    },
  });

  return (
    <>
      <section className="py-20 bg-magnolia">
        <div className="max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl w-full mx-auto px-5 xl:px-0">
          <div className="grid grid-cols-12 gap-7 items-start">
            <div
              className={classNames(
                "col-span-12 lg:col-span-8",
                eventOrganizer?.data?.eventSetting?.detailsBannerLayout ===
                  "reverse"
                  ? "order-1"
                  : ""
              )}
            >
              <div className="lg:py-7 lg:px-5 bg-surface-04 space-y-5">
                {eventSections.map((section, index) => (
                  <div key={index} className="relative">
                    <h2
                      className={`text-deep-purple ${
                        section.tags || section.schedule
                          ? "text-2xl font-semibold"
                          : "text-3xl 2xl:text-4xl font-bold"
                      } capitalize mb-2`}
                    >
                      {section.title}
                    </h2>

                    {section?.content && typeof section.content === "string" ? (
                      <div
                        className="text-lg text-deep-purple font-light space-y-3"
                        dangerouslySetInnerHTML={{ __html: section.content }}
                      />
                    ) : (
                      section.content
                    )}

                    {section.tags && (
                      <div className="flex flex-wrap items-center gap-2">
                        {section.tags.map((tag, i) => (
                          <div
                            key={i}
                            className="px-3 h-10 flex items-center justify-center bg-white rounded-full border border-white capitalize text-text-secondary"
                          >
                            {tag}
                          </div>
                        ))}
                      </div>
                    )}

                    {section.schedule && (
                      <div className="w-full border border-slate-300 rounded-2xl py-5 px-8 bg-white">
                        <div className="flex items-center gap-6 mb-2">
                          <div className="text-text-secondary text-xl font-semibold">
                            {t("Schedule & Venue")}
                          </div>
                          <div className="text-text-secondary text-lg">
                            <i className="fa-regular fa-clock"></i>{" "}
                            {section.schedule.time}
                          </div>
                        </div>
                        <div className="text-text-secondary text-lg">
                          <i className="fa-solid fa-location-dot"></i>{" "}
                          {section.schedule.location}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {event?.artistData?.length > 0 && (
                  <div className="mt-5 space-y-3">
                    <h3 className="font-semibold text-xl">{t("Artists")}</h3>
                    <div className="flex gap-2">
                      {event?.artistData?.map((artist, i) => (
                        <ArtistItem
                          key={artist?.id || artist?._id}
                          artist={artist}
                          variant={"two"}
                          getResponse={getFollowManageRes}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <h3 className="text-deep-purple text-3xl 2xl:text-4xl font-bold capitalize pt-8 !mt-8 !mb-8 border-t border-slate-300 border-dashed">
                  {t("Organized by")}
                </h3>
                <div className="rounded-2xl border border-slate-200 bg-white px-5 md:px-10 py-5 md:py-8">
                  <div className="flex flex-col md:flex-row gap-5 items-start justify-between">
                    <div className="relative flex items-center gap-5">
                      <div className="w-14 h-14 rounded-full border border-primary overflow-hidden bg-primary/10 flex-shrink-0">
                        <Image
                          src={event?.organizationData?.logo?.url}
                          alt={""}
                          classes="object-cover"
                        />
                      </div>
                      <div>
                        <Link
                          to={`/${lang}/organization/details/${event?.organizationData?.uid}`}
                        >
                          <h3 className="text-deep-purple text-xl md:text-2xl 2xl:text-4xl font-bold capitalize">
                            {lngDetect(event?.organizationData?.name) ?? ""}
                          </h3>
                        </Link>

                        {/* Follower count display */}
                        <div className="flex items-center gap-4 mt-2">
                          {/* Follower Count Badge */}
                          <div className="flex items-center gap-2 px-3 py-1 md:py-1.5 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-100 rounded-full shadow-sm hover:shadow-md transition-all duration-200">
                            <div className="flex items-center justify-center w-6 h-6 bg-white rounded-full shadow-sm">
                              <i className="fa-solid fa-users text-purple-600 text-xs"></i>
                            </div>
                            <span className="text-sm font-semibold text-gray-700">
                              {followLoading ? (
                                <div className="inline-block w-12 h-4 bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse rounded-full"></div>
                              ) : (
                                <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
                                  {organizationFollowerCount?.toLocaleString() ||
                                    0}
                                  <span className="ml-1 text-gray-600 font-medium">
                                    {organizationFollowerCount === 1
                                      ? t("Follower")
                                      : t("Followers")}
                                  </span>
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Follow/Following Button */}
                      <Button
                        buttonLabel={t(
                          !isFollowing
                            ? isFollowLoading
                              ? t("Following")
                              : t("Follow")
                            : isFollowLoading
                            ? t("Unfollowing")
                            : t("Unfollow")
                        )}
                        buttonIcon={
                          !isFollowing
                            ? "fa-light fa-user-plus"
                            : "fa-light fa-user-minus"
                        }
                        buttonIconClass={"text-deep-purple"}
                        buttonClasses={
                          "bg-transparent border-deep-purple h-10 md:h-12"
                        }
                        buttonLabelClasses={"text-deep-purple"}
                        buttonFunction={handleFollowToggle}
                        {...(isFollowLoading && {
                          loading: true,
                          buttonIconPosition: "left",
                          isDisable: true,
                        })}
                      />
                      <Button
                        buttonFunction={() => setIsModalOpen(true)}
                        buttonLabel={t("Contact")}
                        buttonIcon={"fa-light fa-paper-plane"}
                        buttonIconPosition="left"
                        buttonClasses={" h-10 md:h-12"}
                      />
                      <ContactModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        event={event}
                      />
                    </div>
                  </div>

                  {/* Rating Section - Only show if there are reviews */}
                  {organizerRatingData.totalReviews > 0 && (
                    <div className="my-4 p-4 bg-gradient-to-r from-primary/10 to-orange-50 rounded-xl border-l-4 border-primary">
                      <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div className="flex flex-col md:flex-row md:items-center gap-3">
                          <RatingStars
                            rating={reviewList?.data?.[0]?.avgRating || 0}
                            totalReviews={organizerRatingData.totalReviews}
                            showCount={true}
                            size="lg"
                          />
                          {organizerRatingData.recommendationPercentage > 0 && (
                            <div className="text-xs text-gray-600 mt-1">
                              {organizerRatingData.recommendationPercentage}%
                              {t("would recommend")}
                            </div>
                          )}
                          {!eventOrganizer?.data?.isReview && (
                            <Button
                              buttonFunction={handleOpenReviewModal}
                              buttonIcon="fa-regular fa-star"
                              buttonIconPosition="left"
                              buttonLabel={t("Write Review")}
                              buttonClasses={
                                "!bg-transparent !rounded-0 !p-0 !m-0 !bg-none gap-1 justify-start md:justify-center"
                              }
                              buttonLabelClasses={"text-purple-500 underline"}
                              buttonIconClass={"text-purple-500 text-sm"}
                            />
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">
                            {t("Recent Reviews")} (
                            {organizerRatingData.totalReviews})
                          </span>
                          <div className="flex items-center gap-3">
                            {organizerRatingData.totalReviews >
                              organizerRatingData.reviews.length && (
                              <span className="text-xs text-gray-500">
                                {t("Showing")} 1 {t("of")}{" "}
                                {organizerRatingData.totalReviews}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* No Reviews State */}
                  {organizerRatingData.totalReviews === 0 &&
                    !reviewsLoading && (
                      <div className="my-4 p-4 bg-gradient-to-r from-primary/10 to-orange-50 rounded-xl border-l-4 border-primary">
                        <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">
                              {t("No reviews yet")}
                            </p>
                            <p className="text-xs text-gray-500">
                              {t("Be the first to review this organizer!")}
                            </p>
                          </div>
                          <Button
                            buttonFunction={handleOpenReviewModal}
                            buttonIcon="fa-regular fa-star"
                            buttonIconPosition="left"
                            buttonLabel={t("Write First Review")}
                            buttonClasses="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 text-sm font-medium rounded-lg transition-all duration-200 border border-blue-200"
                          />
                        </div>
                      </div>
                    )}

                  {/* Loading State */}
                  {reviewsLoading && (
                    <div className="my-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                      </div>
                    </div>
                  )}

                  {/* Recent Reviews Preview */}
                  {organizerRatingData.reviews.length > 0 && (
                    <div className="mt-4 border-t border-dashed border-slate-200 pt-4">
                      {organizerRatingData.reviews.slice(0, 1).map((review) => (
                        <div key={review.id} className="mb-3 last:mb-0">
                          <ReviewPreview review={review} />
                        </div>
                      ))}

                      <Button
                        buttonFunction={handleSeeAllReviews}
                        buttonClasses="px-5 py-2 text-xs transition-all duration-200 font-medium h-auto"
                        buttonLabel={`${t("See all reviews")} →`}
                      />
                    </div>
                  )}
                </div>
              </div>
              <EventReportModal event={event} />
            </div>
            <div
              className={classNames(
                "col-span-12 lg:col-span-4 space-y-5 lg:sticky lg:top-32"
              )}
            >
              {event?.refund?.allowRefundPolicy && (
                <ReturnPolicy
                  data={event?.refund}
                  className={" w-96 lg:w-auto"}
                />
              )}
              {(event?.ticketData || [])?.length > 0 ? (
                <PricingPlanCard
                  isAccountFullyEnabled={isAccountFullyEnabled}
                  isAccountLoading={isAccountLoading}
                  pricingPlan={pricingPlan}
                />
              ) : (
                <div className="bg-white w-full py-6 px-8 rounded-lg flex flex-col items-center justify-center">
                  <h4 className="text-center font-semibold text-deep-purple text-2xl">
                    {t("Tickets will be available soon")}
                  </h4>
                  <NoData image={noDataImage} message="" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Organization Review Modal */}
        <OrganizationReviewModal
          isOpen={isReviewModalOpen}
          onClose={handleCloseReviewModal}
          organizationData={eventOrganizer?.data}
          onSuccess={handleReviewSuccess}
        />

        {/* All Reviews Modal */}
        <AllReviewsModal
          isOpen={isAllReviewsModalOpen}
          onClose={handleCloseAllReviewsModal}
          organizationData={eventOrganizer}
          initialRating={organizerRatingData.rating}
          initialTotalReviews={organizerRatingData.totalReviews}
        />
      </section>
      <button
        onClick={chatStartHandler}
        type="button"
        className="bg-primary text-white w-16 h-16 rounded-full fixed bottom-6 right-6 flex items-center justify-center"
      >
        <i className="fa-solid fa-message text-2xl"></i>
      </button>
    </>
  );
};

export default AboutEvent;
